import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDroppable, useDraggable } from '../KanbanDndContext';
import { Check, User, Mail, Phone, Building2, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getAvatarGradient } from '../communication/personModel';

export interface SidebarPersonDetail {
  personKey?: string;
  name: string;
  role?: string;
  organization?: string;
  email?: string;
  phone?: string;
  taskCount?: number;
  activeVorgangCount?: number;
  hasActiveVorgang?: boolean;
  hasOverdue?: boolean;
}

export function getShortDisplayName(name: string): string {
  if (!name) return '';
  const trimmed = name.trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return trimmed;

  const titleWords = new Set(['dr.', 'dr', 'prof.', 'prof', 'mag.', 'mag', 'di.', 'di', 'ing.', 'ing', 'bsc', 'msc']);
  if (titleWords.has(parts[0].toLowerCase()) && parts.length >= 2) {
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  if (parts.length === 2) {
    return `${parts[0][0]}. ${parts[1]}`;
  }

  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
}

export interface PeopleSidebarItemProps {
  name: string;
  detail?: SidebarPersonDetail;
  isActiveDrag: boolean;
  isDropped: boolean;
  isFilterActive: boolean;
  hasActiveVorgang?: boolean;
  onToggleFilter: (name: string) => void;
  onOpenContactCard: (name: string) => void;
}

const OPEN_DELAY_MS = 200; // Intentional hover delay to prevent strobe effect when skimming across sidebar
const CLOSE_GRACE_MS = 140; // Grace period allowing cursor to move into the tooltip card

export const PeopleSidebarItem: React.FC<PeopleSidebarItemProps> = React.memo(({ 
  name, 
  detail,
  isActiveDrag, 
  isDropped, 
  isFilterActive, 
  hasActiveVorgang = false,
  onToggleFilter, 
  onOpenContactCard 
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState<{ top: number; right: number } | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePopoverCoords = useCallback(() => {
    if (itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      const popoverEstimatedHeight = 220;
      const idealTop = rect.top + rect.height / 2;
      const clampedTop = Math.max(
        popoverEstimatedHeight / 2 + 16,
        Math.min(window.innerHeight - popoverEstimatedHeight / 2 - 16, idealTop)
      );

      setPopoverCoords({
        top: clampedTop,
        right: window.innerWidth - rect.left + 12
      });
    }
  }, []);

  const handleMouseEnter = () => {
    if (isActiveDrag) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (isHovered) return;

    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      updatePopoverCoords();
      setIsHovered(true);
    }, OPEN_DELAY_MS);
  };

  const handleMouseLeave = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsHovered(false);
    }, CLOSE_GRACE_MS);
  };

  const handlePopoverMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isActiveDrag) {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      setIsHovered(false);
    }
  }, [isActiveDrag]);

  const { setNodeRef, isOver } = useDroppable({
    id: `assignee-${name}`,
  });

  const personDragId = detail?.personKey ? `pa::${detail.personKey}` : `pa::${encodeURIComponent(name)}`;
  const { setNodeRef: setDraggableRef, attributes, listeners, isDragging } = useDraggable({
    id: personDragId,
    data: { type: 'person', personName: name, organization: detail?.organization }
  });

  const stopWords = useMemo(() => new Set([
    'dr', 'prof', 'mag', 'di', 'ing', 'bsc', 'msc', 'dipl', 'fh', 'und', 'and', 'or', 'von', 'van', 'der', 'zu', 'gmbh', 'co', 'kg'
  ]), []);

  const initials = useMemo(() => {
    const rawParts = name.split(/[\s.,/\\()_+\-&]+/).filter(Boolean);
    const cleanParts = rawParts.filter(p => !stopWords.has(p.toLowerCase()));
    const parts = cleanParts.length > 0 ? cleanParts : rawParts;

    if (parts.length === 0) return '??';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [name, stopWords]);

  const shortName = useMemo(() => getShortDisplayName(name), [name]);
  const avatarGradient = useMemo(() => getAvatarGradient(detail?.organization, name), [detail?.organization, name]);
  const effectiveHasActiveVorgang = hasActiveVorgang || Boolean(detail?.hasActiveVorgang);

  return (
    <div 
      ref={(node) => {
        setNodeRef(node);
        (itemRef as any).current = node;
      }} 
      className="relative p-1 -m-1 flex flex-col items-center select-none"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        ref={setDraggableRef}
        {...attributes}
        {...listeners}
        onClick={() => onToggleFilter(name)}
        className={`
          group relative flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 border cursor-pointer
          ${isDragging ? 'opacity-30 pointer-events-none' : ''}
          ${isDropped 
            ? 'bg-emerald-500 dark:bg-emerald-600 border-emerald-500 text-white scale-110 shadow-lg shadow-emerald-500/30 z-10 animate-pulse' 
            : isOver 
              ? 'bg-indigo-600/40 border-indigo-400 scale-110 shadow-lg shadow-indigo-500/30 z-10 ring-2 ring-indigo-400' 
              : isFilterActive
                ? 'ring-2 ring-indigo-400 border-transparent scale-105 shadow-md shadow-indigo-500/20'
                : isActiveDrag 
                  ? 'ring-2 ring-dashed ring-indigo-400/40 border-indigo-500/30 border-dashed scale-105 opacity-80' 
                  : 'border-white/10 hover:border-white/30 hover:scale-105 shadow-sm'
          }
        `}
        style={{
          background: isDropped 
            ? undefined 
            : isOver 
              ? undefined 
              : avatarGradient
        }}
      >
        {isDropped ? (
          <Check size={18} className="stroke-[3] text-white" />
        ) : (
          <span className="text-xs font-black text-white drop-shadow-xs tracking-tight">
            {initials}
          </span>
        )}

        {/* Status dot: Rot = Überfälliger Vorgang, Amber/Gelb = Laufender Vorgang / Im Gespräch */}
        {effectiveHasActiveVorgang && !isDropped && (
          <span 
            className={`absolute top-1 left-1 w-2.5 h-2.5 rounded-full border-2 border-slate-900 pointer-events-none ${
              detail?.hasOverdue 
                ? 'bg-rose-500 shadow-sm shadow-rose-500/60 animate-pulse' 
                : 'bg-amber-400 shadow-sm shadow-amber-400/60'
            }`} 
            title={detail?.hasOverdue ? 'Überfälliger Vorgang!' : 'Laufender Vorgang (in Aktion)'}
          />
        )}

        {/* Small contact card button */}
        {!isActiveDrag && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenContactCard(name);
            }}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-900/95 border border-white/20 text-slate-300 hover:text-white hover:bg-indigo-600 hover:border-indigo-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-150 shadow-md shadow-black/50 z-20"
            title={`${name} - Kontaktkarte öffnen`}
          >
            <User size={10} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* Readable name label below avatar */}
      <span 
        onClick={() => onToggleFilter(name)}
        className={`text-[9.5px] font-semibold text-center truncate max-w-[66px] leading-tight px-0.5 mt-1 cursor-pointer transition-colors ${
          isFilterActive 
            ? 'text-indigo-300 font-bold' 
            : 'text-slate-400 hover:text-white'
        }`}
      >
        {shortName}
      </span>

      {/* Instant Premium SaaS Tooltip / Popover via Portal to bypass overflow clipping */}
      {isHovered && popoverCoords && !isActiveDrag && typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, x: 6, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 4, scale: 0.98 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              top: popoverCoords.top,
              right: popoverCoords.right,
              transform: 'translateY(-50%)',
              zIndex: 9999
            }}
            className="w-68 bg-slate-900 border border-slate-700/80 rounded-2xl p-3 shadow-2xl shadow-black/95 ring-1 ring-white/10 text-left select-text cursor-default pointer-events-auto"
            onClick={e => e.stopPropagation()}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
          >
            {/* Caret pointing right towards sidebar */}
            <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 bg-slate-900 border-r border-t border-slate-700/80 pointer-events-none" />

            <div className="flex items-start gap-2.5">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white font-black text-xs shadow-md select-none"
                style={{ background: avatarGradient }}
              >
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-bold text-white leading-tight truncate">
                  {name}
                </h4>
                {(detail?.role || detail?.organization) ? (
                  <div className="flex flex-col gap-0.5 mt-1">
                    {detail.organization && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-slate-300 truncate">
                        <Building2 size={10} className="shrink-0 text-indigo-400" />
                        <span className="truncate">{detail.organization}</span>
                      </span>
                    )}
                    {detail.role && (
                      <span className="text-[10px] text-slate-400 truncate">
                        {detail.role}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Projektkontakt</span>
                )}
              </div>
            </div>

            {/* Status / Task Count badge */}
            <div className="mt-2.5 pt-2 border-t border-white/10 flex items-center justify-between text-[10.5px]">
              {detail?.hasOverdue ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 font-semibold text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                  Frist überfällig {detail?.activeVorgangCount ? `(${detail.activeVorgangCount})` : ''}
                </span>
              ) : effectiveHasActiveVorgang ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  in Aktion {detail?.activeVorgangCount ? `(${detail.activeVorgangCount})` : ''}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-semibold text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Verfügbar
                </span>
              )}
              {detail?.taskCount !== undefined && detail.taskCount > 0 && (
                <span className="text-[10px] text-slate-400 font-medium">
                  {detail.taskCount} {detail.taskCount === 1 ? 'Aufgabe' : 'Aufgaben'}
                </span>
              )}
            </div>

            {/* Contact info: Mail & Phone */}
            {(detail?.email || detail?.phone) && (
              <div className="mt-2 pt-2 border-t border-white/10 flex flex-col gap-1 text-[10.5px]">
                {detail.email && (
                  <a 
                    href={`mailto:${detail.email}`}
                    className="flex items-center gap-1.5 text-indigo-300 hover:text-indigo-200 hover:underline truncate py-0.5"
                    title={`E-Mail: ${detail.email}`}
                  >
                    <Mail size={11} className="shrink-0 text-slate-400" />
                    <span className="truncate">{detail.email}</span>
                  </a>
                )}
                {detail.phone && (
                  <a 
                    href={`tel:${detail.phone}`}
                    className="flex items-center gap-1.5 text-slate-300 hover:text-white hover:underline truncate py-0.5"
                    title={`Telefon: ${detail.phone}`}
                  >
                    <Phone size={11} className="shrink-0 text-slate-400" />
                    <span className="truncate">{detail.phone}</span>
                  </a>
                )}
              </div>
            )}

            {/* Actions row */}
            <div className="mt-2.5 pt-2 border-t border-white/10 flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFilter(name);
                  setIsHovered(false);
                }}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-xl text-[10px] font-semibold transition-all ${
                  isFilterActive 
                    ? 'bg-indigo-600 text-white shadow-xs' 
                    : 'bg-white/10 hover:bg-white/15 text-slate-200'
                }`}
              >
                <Filter size={10} />
                <span>{isFilterActive ? 'Filter aufheben' : 'Filtern'}</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenContactCard(name);
                  setIsHovered(false);
                }}
                className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-xl bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-500/30 text-[10px] font-semibold transition-colors"
                title="Kontaktkarte öffnen"
              >
                <User size={10} />
                <span>Details</span>
              </button>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
});
